// ─────────────────────────────────────────────────────────────────────────────
// JumpPlayer — plays a SEQUENCE of [start,end] ranges of public YouTube
// videos as one continuous supercut, via the official IFrame Player API.
// Nothing is downloaded, extracted, or re-hosted: every frame streams from
// YouTube's own embed of the untouched original, ads/attribution intact.
// PROTOTYPE (clip player).
// ─────────────────────────────────────────────────────────────────────────────

import {useEffect, useRef, useState} from 'react'

import type {CutSegment} from '../shared/cuts'
import {loadIframeApi, type YTPlayer} from './youtube'

export function JumpPlayer({segments}: {segments: CutSegment[]}) {
  const mountRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<YTPlayer | null>(null)
  const indexRef = useRef(0)
  const startedRef = useRef(false)
  const [index, setIndex] = useState(0)
  const [status, setStatus] = useState<'loading' | 'ready' | 'done' | 'error'>('loading')

  // Advancing on ENDED needs the CURRENT segment list; keep it in a ref so the
  // player (created once) never closes over a stale array.
  const segmentsRef = useRef(segments)
  segmentsRef.current = segments

  const playSegment = (i: number) => {
    const seg = segmentsRef.current[i]
    if (!seg || !playerRef.current) return
    indexRef.current = i
    setIndex(i)
    setStatus('ready')
    playerRef.current.loadVideoById({
      videoId: seg.videoId,
      startSeconds: seg.startSec,
      endSeconds: seg.endSec,
    })
    startedRef.current = true
  }

  useEffect(() => {
    let cancelled = false
    loadIframeApi().then(YT => {
      if (cancelled || !mountRef.current) return
      const first = segmentsRef.current[0]
      if (!first) return
      playerRef.current = new YT.Player(mountRef.current, {
        width: '100%',
        height: '100%',
        playerVars: {rel: 0, modestbranding: 1, playsinline: 1},
        events: {
          onReady: () => {
            setStatus('ready')
            playerRef.current?.cueVideoById({
              videoId: first.videoId,
              startSeconds: first.startSec,
              endSeconds: first.endSec,
            })
          },
          onStateChange: e => {
            if (e.data === YT.PlayerState.PLAYING) startedRef.current = true
            if (e.data === YT.PlayerState.ENDED && startedRef.current) {
              const next = indexRef.current + 1
              if (next < segmentsRef.current.length) playSegment(next)
              else setStatus('done')
            }
          },
          onError: () => {
            // Unplayable/removed video: skip forward rather than stall the cut.
            const next = indexRef.current + 1
            if (next < segmentsRef.current.length) playSegment(next)
            else setStatus('error')
          },
        },
      })
    })
    return () => {
      cancelled = true
      playerRef.current?.destroy()
      playerRef.current = null
    }
    // The player mounts once; segment changes flow through segmentsRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const current = segments[index]

  return (
    <div className="jump-player">
      <div className="jp-frame">
        <div ref={mountRef} />
      </div>
      <div className="jp-bar">
        <span className="jp-count">
          {status === 'done' ? 'that’s the cut' : `segment ${index + 1} of ${segments.length}`}
        </span>
        {current?.episodeTitle ? <span className="jp-ep">{current.episodeTitle}</span> : null}
        {status === 'done' ? (
          <button className="jp-replay" onClick={() => playSegment(0)}>
            replay
          </button>
        ) : null}
      </div>
      <ol className="jp-list">
        {segments.map((seg, i) => (
          <li key={`${seg.videoId}-${seg.startSec}`} className={i === index ? 'current' : ''}>
            <button onClick={() => playSegment(i)}>
              <span className="jp-ts">{seg.timestamp}</span>
              <span className="jp-why">
                {seg.title ? <b>{seg.title} — </b> : null}
                {seg.why}
              </span>
              {seg.episodeTitle ? <span className="jp-src">{seg.episodeTitle}</span> : null}
            </button>
          </li>
        ))}
      </ol>
    </div>
  )
}
